import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Dashboard from "./pages/Dashboard";
import ConsolidatedVerdict from "./pages/ConsolidatedVerdict";
import Window64Confluence from "./pages/Window64Confluence";
import LoginPage from "./pages/LoginPage";
import Window3PerBall from "./pages/Window3PerBall";
import Window3_1PatternAX from "./pages/Window3_1PatternAX";
import Window5SevenCombo from "./pages/Window5SevenCombo";
import Window6MonthlyFrequency from "./pages/Window6MonthlyFrequency";
import Window7NumberAppearance from "./pages/Window7NumberAppearance";
import Window8BeforeAfter from "./pages/Window8BeforeAfter";
import Window9LSDifference from "./pages/Window9LSDifference";
import Window10LSAppearance from "./pages/Window10LSAppearance";
import LSPrediction from "./pages/LSPrediction";
import Window43LSSpreadDiff from "./pages/Window43LSSpreadDiff";
import Window11PositionalHotCold from "./pages/Window11PositionalHotCold";
import WindowCrossAnalysis from "./pages/WindowCrossAnalysis";
import MasterPrediction from "./pages/MasterPrediction";
import PatternPredictor from "./pages/PatternPredictor";
import Window14SumRange from "./pages/Window14SumRange";
import Window18RollingWindow from "./pages/Window18RollingWindow";
import NotFoundPage from "./pages/NotFoundPage";
import Window27OutlierDetection from "./pages/Window27OutlierDetection";
import Window29DigitPosition from "./pages/Window29DigitPosition";
import Window4TrueFalseZero from "./pages/Window4TrueFalseZero";
import Window12Consecutive from "./pages/Window12Consecutive";
import Window13GapAnalysis from "./pages/Window13GapAnalysis";
import Window16PairCorrelation from "./pages/Window16PairCorrelation";

import Window19MLPatterns from "./pages/Window19MLPatterns";
import Window20SpreadDistribution from "./pages/Window20SpreadDistribution";
import Window21DecayFrequency from "./pages/Window21DecayFrequency";
import Window22DrawSimilarity from "./pages/Window22DrawSimilarity";
import Window23Entropy from "./pages/Window23Entropy";
import Window24RangeBias from "./pages/Window24RangeBias";
import Window25MirrorComplement from "./pages/Window25MirrorComplement";
import Window28CombinationValidator from "./pages/Window28CombinationValidator";
import WindowCalendarPatterns from "./pages/WindowCalendarPatterns";
import Window32PrimeAnalysis from "./pages/Window32PrimeAnalysis";
import Window65Method2 from "./pages/Window65Method2";
import Window37Yoga from "./pages/Window37Yoga";
import { W37ErrorBoundary } from "./components/W37ErrorBoundary";

import SummaryDashboard from "./pages/SummaryDashboard";
import WeightsPage from "./pages/WeightsPage";
import SignalsPage from "./pages/SignalsPage";
import PredictionChecker from "./pages/PredictionChecker";
import PredictionMaker from "./pages/PredictionMaker";
import WindowClassMix from "./pages/WindowClassMix";
import NumberTierConsensus from "./pages/NumberTierConsensus";
import MyPicks from "./pages/MyPicks";
import WindowAccuracy from "./pages/WindowAccuracy";
import WindowAdjacentPairs from "./pages/WindowAdjacentPairs";
import WindowTypes126 from "./pages/WindowTypes126";
import WindowLSAdvanced from "./pages/WindowLSAdvanced";
import WindowDigitAffinity from "./pages/WindowDigitAffinity";
import DrawValidationTracker from "./pages/DrawValidationTracker";
import WindowBallDiff from "./pages/WindowBallDiff";
import WindowDrawDNA from "./pages/WindowDrawDNA";
import DNAValidation from "./pages/DNAValidation";
import MyPredictionMaker from "./pages/MyPredictionMaker";
import WinnerData from "./pages/WinnerData";
import ReboundAlert from "./pages/ReboundAlert";
import { CircleNotch } from "@phosphor-icons/react";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CircleNotch size={32} className="animate-spin text-[#002FA7]" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Auth Route - redirect to home if already logged in
const AuthRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CircleNotch size={32} className="animate-spin text-[#002FA7]" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  // Fallback for pages without their own `useDrawsUpdated` hook:
  // when a draw is added/edited/deleted, remount the active route
  // so it refetches with the latest data. Pages that *do* have the
  // hook mark the event as handled and skip this remount, preserving
  // their scroll position and local UI state.
  const [drawsVersion, setDrawsVersion] = useState(0);
  useEffect(() => {
    const handler = (e) => {
      // Let any page-level `useDrawsUpdated` handlers run first; if
      // none flagged the event as handled, remount the active route.
      setTimeout(() => {
        if (!e || !e.__handled) setDrawsVersion(v => v + 1);
      }, 0);
    };
    window.addEventListener('euromillions:draws-updated', handler);
    return () => window.removeEventListener('euromillions:draws-updated', handler);
  }, []);

  return (
    <Routes key={drawsVersion}>
      <Route
        path="/login"
        element={
          <AuthRoute>
            <LoginPage />
          </AuthRoute>
        }
      />
      <Route element={<ProtectedRoute><Outlet /></ProtectedRoute>}>
        <Route path="/" element={<Dashboard />} />
      <Route path="/consolidated-verdict" element={<ConsolidatedVerdict />} />
      <Route path="/window63" element={<Navigate to="/rebound-alert?tab=reversion" replace />} />
      <Route path="/window64" element={<Window64Confluence />} />
      <Route path="/window2" element={<Navigate to="/window3" replace />} />
      <Route path="/window3" element={<Window3PerBall />} />
      <Route path="/window3-pattern" element={<Window3_1PatternAX />} />
      <Route path="/window3-3" element={<Window4TrueFalseZero />} />
      <Route path="/window3-2" element={<Window5SevenCombo />} />
      <Route path="/window6" element={<Window6MonthlyFrequency />} />
      <Route path="/window7" element={<Window7NumberAppearance />} />
      <Route path="/window8" element={<Window8BeforeAfter />} />
      <Route path="/window9" element={<Window9LSDifference />} />
      <Route path="/window10" element={<Window10LSAppearance />} />
      <Route path="/ls-prediction" element={<LSPrediction />} />
      <Route path="/window43" element={<Window43LSSpreadDiff />} />
      <Route path="/window11" element={<Window11PositionalHotCold />} />
      <Route path="/window14" element={<Window14SumRange />} />
      <Route path="/window18" element={<Window18RollingWindow />} />
      <Route path="/window12" element={<Window12Consecutive />} />
      <Route path="/window13" element={<Window13GapAnalysis />} />
      <Route path="/window17" element={<WindowCalendarPatterns />} />
      <Route path="/window16" element={<Window16PairCorrelation />} />
      <Route path="/window19" element={<Window19MLPatterns />} />
      <Route path="/window20" element={<Window20SpreadDistribution />} />
      <Route path="/window21" element={<Window21DecayFrequency />} />
      <Route path="/window22" element={<Window22DrawSimilarity />} />
      <Route path="/window23" element={<Window23Entropy />} />
      <Route path="/window24" element={<Window24RangeBias />} />
      <Route path="/window25" element={<Window25MirrorComplement />} />
      <Route path="/window27" element={<Window27OutlierDetection />} />
      <Route path="/window28" element={<Window28CombinationValidator />} />
      <Route path="/window29" element={<Window29DigitPosition />} />
      <Route path="/window30" element={<Navigate to="/window7?view=cycles" replace />} />
      <Route path="/window31" element={<Navigate to="/window17" replace />} />
      <Route path="/window32" element={<Window32PrimeAnalysis />} />

      <Route path="/ls-advanced" element={<WindowLSAdvanced />} />
      <Route path="/summary" element={<SummaryDashboard />} />
      <Route path="/weights" element={<WeightsPage />} />
      <Route path="/signals" element={<SignalsPage />} />
      <Route path="/prediction-checker" element={<PredictionChecker />} />
      <Route path="/prediction-maker" element={<PredictionMaker />} />
      <Route path="/my-prediction-maker" element={<MyPredictionMaker />} />
      <Route path="/winner-data" element={<WinnerData />} />
      <Route path="/rebound-alert" element={<ReboundAlert />} />
      <Route path="/class-mix" element={<WindowClassMix />} />
      <Route path="/number-tier-consensus" element={<NumberTierConsensus />} />
      <Route path="/date-digit-analysis" element={<Navigate to="/window17" replace />} />
      <Route path="/my-picks" element={<MyPicks />} />
      <Route path="/window-accuracy" element={<WindowAccuracy />} />
      <Route path="/adjacent-pairs" element={<WindowAdjacentPairs />} />
      <Route path="/types" element={<WindowTypes126 />} />
      <Route path="/digit-affinity" element={<WindowDigitAffinity />} />
      <Route path="/draw-validation" element={<DrawValidationTracker />} />
      <Route path="/ball-diff" element={<WindowBallDiff />} />
      <Route path="/draw-dna" element={<WindowDrawDNA />} />
      <Route path="/dna-validation" element={<DNAValidation />} />
      <Route path="/cross-analysis" element={<WindowCrossAnalysis />} />
      <Route path="/master-prediction" element={<MasterPrediction />} />
      <Route path="/window65" element={<Window65Method2 />} />
      <Route path="/window37" element={<W37ErrorBoundary><Window37Yoga /></W37ErrorBoundary>} />
      <Route path="/pattern-predictor" element={<PatternPredictor />} />
      <Route path="/calendar-patterns" element={<Navigate to="/window17" replace />} />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
